import React, { useEffect, useState, useCallback, useMemo } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import Swal from "sweetalert2";
import moment from "moment";
import "moment/locale/es";
import {
  AiOutlineUser, AiOutlineTeam, AiOutlineIdcard, AiOutlineBook,
  AiOutlineImport, AiOutlinePlus, AiOutlineEdit, AiOutlineDelete, AiOutlineEye, AiOutlineLineChart,
} from "react-icons/ai";
import { MdLockReset } from "react-icons/md";
import Header from "../../components/protected/Header";
import Footer from "../../components/footer";
import { auth } from "../../config/firebase";
import { useTranslations } from "../../translations";
import { useSchoolRole } from "../../hooks/useSchoolRole";
import { StudentActivityModal } from "../../components/protected/activity/StudentActivity";

const API_BASE = (
  process.env.REACT_APP_BLOOMS_API_URL || "https://ai-blooms.vercel.app/api/openai/blooms"
).replace(/\/api\/.*/, "");

const PER_PAGE = 8;
const DOC_TYPES = ["V", "E", "P", "J", "G"];

const genPassword = () => {
  const b = new Uint8Array(9);
  window.crypto.getRandomValues(b);
  return btoa(String.fromCharCode(...b)).replace(/[+/=]/g, "").slice(0, 12);
};

const emailFromDoc = (docType, docNumber, domain) =>
  docNumber ? `${(docType || "V").toLowerCase()}${docNumber}@${domain}` : "";

/** Parsea la lista pegada: detecta correo (@) y cédula (V-1234567); el resto es el nombre. */
const parseImport = (text) =>
  text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((line) => {
    const parts = line.split(/\t|;|,/).map((p) => p.trim()).filter(Boolean);
    const tokens = parts.length > 1 ? parts : line.split(/\s{2,}/).map((p) => p.trim());
    let email = "", docType = "", docNumber = "";
    const nameParts = [];
    tokens.forEach((tok) => {
      if (!email && tok.includes("@")) { email = tok.toLowerCase(); return; }
      const m = tok.match(/^([VEPJG])?[-\s]?(\d{4,12})$/i);
      if (!docNumber && m) { docType = (m[1] || "V").toUpperCase(); docNumber = m[2]; return; }
      nameParts.push(tok);
    });
    return { displayName: nameParts.join(" ").trim(), docType, docNumber, email };
  });

// ── Monograma (avatar con iniciales, color estable por nombre) ──
const MONO = [
  ["#eb2178", "#ff7bb0"], ["#632d43", "#a24d6f"], ["#2e7d6b", "#5bc0aa"],
  ["#3b5bdb", "#7d97ff"], ["#d9770a", "#ffb64d"], ["#7048e8", "#a487ff"],
];
const initials = (name = "") => {
  const w = String(name).trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "—";
  return ((w[0][0] || "") + (w[1]?.[0] || w[0][1] || "")).toUpperCase();
};
const hashIdx = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % MONO.length;
};
const Monogram = ({ name }) => {
  const [a, b] = MONO[hashIdx(name || "")];
  return (
    <span className="sp-mono-av" style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}>
      {initials(name)}
    </span>
  );
};

const Pill = ({ tone = "grade", children }) =>
  children ? <span className={`sp-pill sp-pill-${tone}`}>{children}</span>
           : <span className="sp-pill sp-pill-none">—</span>;

const Pager = ({ t, page, total, perPage, onPage }) => {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  if (totalPages <= 1) return null;
  return (
    <div className="sp-pager">
      <button type="button" className="sp-page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Anterior">‹</button>
      <span className="sp-page-info">{t("pageWord")} {page} / {totalPages}</span>
      <button type="button" className="sp-page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)} aria-label="Siguiente">›</button>
    </div>
  );
};

const EmptyState = ({ icon, title, hint }) => (
  <div className="sp-empty">
    <div className="sp-empty-ic">{icon}</div>
    <div className="sp-empty-t">{title}</div>
    {hint ? <div className="sp-empty-h">{hint}</div> : null}
  </div>
);

const SchoolPanel = ({ view = "teachers" }) => {
  const { t } = useTranslations();
  const { role, loading: roleLoading } = useSchoolRole();
  const isSchool = role === "escuela";

  const [teachers, setTeachers] = useState([]);
  const [sections, setSections] = useState([]);
  const [grades, setGrades] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [tPage, setTPage] = useState(1);
  const [gPage, setGPage] = useState(1);
  const [sPage, setSPage] = useState(1);
  const [uPage, setUPage] = useState(1);

  // Formulario profesor
  const [tName, setTName] = useState("");
  const [tDocType, setTDocType] = useState("V");
  const [tDocNumber, setTDocNumber] = useState("");
  const [tEmail, setTEmail] = useState("");
  const [tPass, setTPass] = useState(genPassword());
  const [savingT, setSavingT] = useState(false);

  // Formulario grado
  const [gName, setGName] = useState("");
  const [savingG, setSavingG] = useState(false);

  // Formulario sección
  const [sName, setSName] = useState("");
  const [sGradeId, setSGradeId] = useState("");
  const [sTeachers, setSTeachers] = useState([]);
  const [savingS, setSavingS] = useState(false);

  // Formulario alumno
  const [uName, setUName] = useState("");
  const [uDocType, setUDocType] = useState("V");
  const [uDocNumber, setUDocNumber] = useState("");
  const [uEmail, setUEmail] = useState("");
  const [uPass, setUPass] = useState(genPassword());
  const [uGradeId, setUGradeId] = useState("");
  const [uSectionId, setUSectionId] = useState("");
  const [savingU, setSavingU] = useState(false);

  // Importador
  const [showImport, setShowImport] = useState(false);
  const [importMode, setImportMode] = useState("teachers"); // teachers | students
  const [importText, setImportText] = useState("");
  const [importDomain, setImportDomain] = useState("docentes.miabc.online");
  const [importAutoEmail, setImportAutoEmail] = useState(false);
  const [importGradeId, setImportGradeId] = useState("");
  const [importSectionId, setImportSectionId] = useState("");
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const headers = useCallback(async () => {
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken(true) : null;
    return { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const h = await headers();
      const [tr, sr, gr, ur] = await Promise.all([
        fetch(`${API_BASE}/api/school/teachers`, { headers: h }).then((r) => r.json()),
        fetch(`${API_BASE}/api/school/sections`, { headers: h }).then((r) => r.json()),
        fetch(`${API_BASE}/api/school/grades`, { headers: h }).then((r) => r.json()),
        fetch(`${API_BASE}/api/school/students`, { headers: h }).then((r) => r.json()),
      ]);
      setTeachers(tr.teachers || []);
      setSections(sr.sections || []);
      setGrades(gr.grades || []);
      setStudents(ur.students || []);
    } catch (e) {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (!roleLoading && isSchool) load();
  }, [roleLoading, isSchool, load]);

  const err = (e) => Swal.fire({ icon: "error", title: t("errorTitle"), text: e.message });
  const teacherName = (uid) => {
    const f = teachers.find((x) => x.uid === uid);
    return f ? f.displayName || f.email : uid;
  };

  // ── Profesores ──
  const createTeacher = async (e) => {
    e.preventDefault();
    try {
      setSavingT(true);
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/teachers`, {
        method: "POST", headers: h,
        body: JSON.stringify({
          email: tEmail.trim(), password: tPass, displayName: tName.trim(),
          docType: tDocNumber.trim() ? tDocType : undefined, docNumber: tDocNumber.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");
      Swal.fire({ icon: "success", title: t("teacherCreated"), html: `<div style="text-align:left">${tEmail}<br/>${t("adminPasswordLabel")}: <code>${tPass}</code></div>` });
      setTName(""); setTEmail(""); setTDocNumber(""); setTDocType("V"); setTPass(genPassword());
      load();
    } catch (e2) { err(e2); } finally { setSavingT(false); }
  };
  const resetTeacher = async (tc) => {
    const c = await Swal.fire({
      title: t("resetPasswordConfirm"), html: `<b>${tc.displayName || tc.email}</b><br/>${t("resetPasswordConfirmText")}`,
      icon: "question", showCancelButton: true, confirmButtonText: t("resetPassword"), cancelButtonText: t("cancel"), confirmButtonColor: "#eb2178",
    });
    if (!c.isConfirmed) return;
    try {
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/teachers`, { method: "PATCH", headers: h, body: JSON.stringify({ uid: tc.uid, resetPassword: true }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");
      Swal.fire({ icon: "success", title: t("newPasswordGenerated"), html: `${tc.email}<br/><code>${data.password}</code>` });
    } catch (e2) { err(e2); }
  };
  const deleteTeacher = async (tc) => {
    const c = await Swal.fire({ title: t("deleteTeacherConfirm"), text: tc.email, icon: "warning", showCancelButton: true });
    if (!c.isConfirmed) return;
    try {
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/teachers?uid=${tc.uid}`, { method: "DELETE", headers: h });
      if (!res.ok) throw new Error((await res.json()).error || "Error");
      load();
    } catch (e2) { err(e2); }
  };
  const [detailTeacher, setDetailTeacher] = useState(null);
  const [editTeacher, setEditTeacher] = useState(null);
  const [etName, setEtName] = useState("");
  const [etDocType, setEtDocType] = useState("V");
  const [etDocNumber, setEtDocNumber] = useState("");
  const [savingET, setSavingET] = useState(false);
  const openEditTeacher = (tc) => {
    setEditTeacher(tc);
    setEtName(tc.displayName || "");
    setEtDocType(tc.docType || "V");
    setEtDocNumber(tc.docNumber || "");
  };
  const saveEditTeacher = async (e) => {
    e.preventDefault();
    try {
      setSavingET(true);
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/teachers`, {
        method: "PATCH", headers: h,
        body: JSON.stringify({
          uid: editTeacher.uid, displayName: etName.trim(),
          docType: etDocNumber.trim() ? etDocType : "", docNumber: etDocNumber.trim(),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Error");
      setEditTeacher(null); load();
    } catch (e2) { err(e2); } finally { setSavingET(false); }
  };

  // ── Grados ──
  const createGrade = async (e) => {
    e.preventDefault();
    try {
      setSavingG(true);
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/grades`, { method: "POST", headers: h, body: JSON.stringify({ name: gName.trim() }) });
      if (!res.ok) throw new Error((await res.json()).error || "Error");
      setGName(""); load();
    } catch (e2) { err(e2); } finally { setSavingG(false); }
  };
  const renameGrade = async (g) => {
    const r = await Swal.fire({ title: t("editGrade"), input: "text", inputValue: g.name, showCancelButton: true });
    if (!r.isConfirmed || !r.value) return;
    try {
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/grades`, { method: "PATCH", headers: h, body: JSON.stringify({ id: g.id, name: r.value.trim() }) });
      if (!res.ok) throw new Error((await res.json()).error || "Error");
      load();
    } catch (e2) { err(e2); }
  };
  const deleteGrade = async (g) => {
    const c = await Swal.fire({ title: t("deleteGradeConfirm"), text: g.name, icon: "warning", showCancelButton: true });
    if (!c.isConfirmed) return;
    try {
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/grades?id=${g.id}`, { method: "DELETE", headers: h });
      const data = await res.json();
      if (res.status === 409) return Swal.fire({ icon: "warning", title: t("gradeHasSections"), text: `${data.count}` });
      if (!res.ok) throw new Error(data.error || "Error");
      load();
    } catch (e2) { err(e2); }
  };

  // ── Secciones ──
  const toggleTeacher = (uid) => setSTeachers((p) => (p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]));
  const createSection = async (e) => {
    e.preventDefault();
    try {
      setSavingS(true);
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/sections`, { method: "POST", headers: h, body: JSON.stringify({ name: sName.trim(), gradeId: sGradeId, teacherIds: sTeachers }) });
      if (!res.ok) throw new Error((await res.json()).error || "Error");
      setSName(""); setSGradeId(""); setSTeachers([]); load();
    } catch (e2) { err(e2); } finally { setSavingS(false); }
  };
  const [detailSec, setDetailSec] = useState(null);
  const [dPage, setDPage] = useState(1);
  const openDetail = (sec) => { setDetailSec(sec); setDPage(1); };

  const [editSec, setEditSec] = useState(null);
  const [eName, setEName] = useState("");
  const [eGradeId, setEGradeId] = useState("");
  const [eTeachers, setETeachers] = useState([]);
  const [eStudents, setEStudents] = useState([]);
  const [savingE, setSavingE] = useState(false);
  const openEditSection = (sec) => {
    setEditSec(sec);
    setEName(sec.name || "");
    setEGradeId(sec.gradeId || "");
    setETeachers(sec.teacherIds ? Object.keys(sec.teacherIds) : []);
    setEStudents(students.filter((s) => s.sectionId === sec.id).map((s) => s.uid));
  };
  const toggleETeacher = (uid) => setETeachers((p) => (p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]));
  const toggleEStudent = (uid) => setEStudents((p) => (p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]));
  const saveEditSection = async (e) => {
    e.preventDefault();
    try {
      setSavingE(true);
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/sections`, {
        method: "PATCH", headers: h,
        body: JSON.stringify({ id: editSec.id, name: eName.trim(), gradeId: eGradeId, teacherIds: eTeachers, studentIds: eStudents }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Error");
      setEditSec(null); load();
    } catch (e2) { err(e2); } finally { setSavingE(false); }
  };
  const deleteSection = async (sec) => {
    const c = await Swal.fire({ title: t("deleteSectionConfirm"), text: sec.name, icon: "warning", showCancelButton: true });
    if (!c.isConfirmed) return;
    try {
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/sections?id=${sec.id}`, { method: "DELETE", headers: h });
      const data = await res.json();
      if (res.status === 409) return Swal.fire({ icon: "warning", title: t("sectionHasStudents"), text: `${data.count}` });
      if (!res.ok) throw new Error(data.error || "Error");
      load();
    } catch (e2) { err(e2); }
  };

  // ── Alumnos ──
  const sectionsForGrade = (gradeId) => sections.filter((s) => !gradeId || s.gradeId === gradeId);
  const createStudent = async (e) => {
    e.preventDefault();
    if (!uGradeId) return err(new Error(t("selectGrade")));
    try {
      setSavingU(true);
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/students`, {
        method: "POST", headers: h,
        body: JSON.stringify({
          learnerName: uName.trim(), email: uEmail.trim(), password: uPass,
          gradeId: uGradeId, sectionId: uSectionId || undefined,
          docType: uDocNumber.trim() ? uDocType : undefined, docNumber: uDocNumber.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");
      Swal.fire({ icon: "success", title: t("studentCreated"), html: `<div style="text-align:left"><b>${uName}</b><br/>${uEmail}<br/>${t("adminPasswordLabel")}: <code>${uPass}</code></div>` });
      setUName(""); setUEmail(""); setUDocNumber(""); setUDocType("V"); setUSectionId(""); setUPass(genPassword());
      load();
    } catch (e2) { err(e2); } finally { setSavingU(false); }
  };
  const [editStu, setEditStu] = useState(null);
  const [euName, setEuName] = useState("");
  const [euGradeId, setEuGradeId] = useState("");
  const [euSectionId, setEuSectionId] = useState("");
  const [euDocType, setEuDocType] = useState("V");
  const [euDocNumber, setEuDocNumber] = useState("");
  const [savingEU, setSavingEU] = useState(false);
  const openEditStudent = (s) => {
    setEditStu(s);
    setEuName(s.learnerName || "");
    setEuGradeId(s.gradeId || "");
    setEuSectionId(s.sectionId || "");
    setEuDocType(s.docType || "V");
    setEuDocNumber(s.docNumber || "");
  };
  const saveEditStudent = async (e) => {
    e.preventDefault();
    try {
      setSavingEU(true);
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/students`, {
        method: "PATCH", headers: h,
        body: JSON.stringify({
          uid: editStu.uid, learnerName: euName.trim(), gradeId: euGradeId,
          sectionId: euSectionId || null,
          docType: euDocNumber.trim() ? euDocType : "", docNumber: euDocNumber.trim(),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || "Error");
      setEditStu(null); load();
    } catch (e2) { err(e2); } finally { setSavingEU(false); }
  };
  const resetStudent = async (s) => {
    const c = await Swal.fire({
      title: t("resetPasswordConfirm"), html: `<b>${s.learnerName}</b><br/>${t("resetPasswordConfirmText")}`,
      icon: "question", showCancelButton: true, confirmButtonText: t("resetPassword"), cancelButtonText: t("cancel"), confirmButtonColor: "#eb2178",
    });
    if (!c.isConfirmed) return;
    try {
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/students`, { method: "PATCH", headers: h, body: JSON.stringify({ uid: s.uid, resetPassword: true }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");
      Swal.fire({ icon: "success", title: t("newPasswordGenerated"), html: `${s.email}<br/><code>${data.password}</code>` });
    } catch (e2) { err(e2); }
  };
  const deleteStudent = async (s) => {
    const c = await Swal.fire({ title: t("deleteStudentConfirm"), text: s.learnerName, icon: "warning", showCancelButton: true });
    if (!c.isConfirmed) return;
    try {
      const h = await headers();
      const res = await fetch(`${API_BASE}/api/school/students?uid=${s.uid}`, { method: "DELETE", headers: h });
      if (!res.ok) throw new Error((await res.json()).error || "Error");
      load();
    } catch (e2) { err(e2); }
  };
  const [detailStu, setDetailStu] = useState(null);
  const [actStu, setActStu] = useState(null);

  // ── Importador (profesores o alumnos) ──
  const openImport = (mode) => {
    setImportMode(mode);
    setImportText(""); setImportReport(null);
    setImportGradeId(mode === "students" && grades[0] ? grades[0].id : "");
    setImportSectionId("");
    setShowImport(true);
  };
  const closeImport = () => { setShowImport(false); setImportText(""); setImportReport(null); };
  const importRows = useMemo(() => {
    return parseImport(importText).map((r) => {
      let email = r.email;
      if (!email && importAutoEmail) email = emailFromDoc(r.docType, r.docNumber, importDomain.trim());
      const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || "");
      const valid = !!r.displayName && validEmail;
      return {
        displayName: r.displayName, docType: r.docNumber ? r.docType || "V" : "", docNumber: r.docNumber,
        email: email || "", password: genPassword(), valid,
        reason: !r.displayName ? t("importNoName") : !validEmail ? t("importNoEmail") : "",
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importText, importAutoEmail, importDomain]);
  const importValidCount = importRows.filter((r) => r.valid).length;
  const importReady = importValidCount > 0 && (importMode !== "students" || !!importGradeId);
  const doImport = async () => {
    const rows = importRows.filter((r) => r.valid);
    if (!rows.length) return;
    try {
      setImporting(true);
      const h = await headers();
      let url, payload;
      if (importMode === "students") {
        url = `${API_BASE}/api/school/students/import`;
        payload = {
          students: rows.map((r) => ({ learnerName: r.displayName, email: r.email, password: r.password, docType: r.docType || undefined, docNumber: r.docNumber || undefined })),
          gradeId: importGradeId, sectionId: importSectionId || undefined,
        };
      } else {
        url = `${API_BASE}/api/school/teachers/import`;
        payload = { teachers: rows.map((r) => ({ displayName: r.displayName, email: r.email, password: r.password, docType: r.docType || undefined, docNumber: r.docNumber || undefined })) };
      }
      const res = await fetch(url, { method: "POST", headers: h, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");
      setImportReport(data); load();
    } catch (e2) { err(e2); } finally { setImporting(false); }
  };
  const copyCredentials = () => {
    const created = (importReport?.results || []).filter((r) => r.status === "created");
    const txt = created.map((r) => `${r.displayName || r.learnerName || ""}\t${r.email}\t${r.password}`).join("\n");
    navigator.clipboard?.writeText(txt);
    Swal.fire({ icon: "success", title: t("copied"), timer: 1200, showConfirmButton: false });
  };

  // ── Paginación ──
  const clamp = (p, total) => Math.min(p, Math.max(1, Math.ceil(total / PER_PAGE)));
  const pT = clamp(tPage, teachers.length), pG = clamp(gPage, grades.length);
  const pS = clamp(sPage, sections.length), pU = clamp(uPage, students.length);
  const teachersPage = teachers.slice((pT - 1) * PER_PAGE, pT * PER_PAGE);
  const gradesPage = grades.slice((pG - 1) * PER_PAGE, pG * PER_PAGE);
  const sectionsPage = sections.slice((pS - 1) * PER_PAGE, pS * PER_PAGE);
  const studentsPage = students.slice((pU - 1) * PER_PAGE, pU * PER_PAGE);

  const META = {
    teachers: { title: t("teachers"), desc: t("teachersDesc"), icon: <AiOutlineUser /> },
    grades: { title: t("grades"), desc: t("gradesDesc"), icon: <AiOutlineBook /> },
    sections: { title: t("sections"), desc: t("sectionsDesc"), icon: <AiOutlineTeam /> },
    students: { title: t("students"), desc: t("studentsDesc"), icon: <AiOutlineIdcard /> },
  }[view] || {};

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
        <Container fluid className="py-4 mx-auto sp-wrap">
          {/* Encabezado de página */}
          <div className="sp-head">
            <div className="sp-head-ic">{META.icon}</div>
            <div>
              <div className="sp-eyebrow">{t("schoolPanelTitle")}</div>
              <h2 className="sp-title">{META.title}</h2>
            </div>
          </div>
          {META.desc ? <p className="sp-desc">{META.desc}</p> : null}

          {roleLoading || loading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: "var(--sp-plum)" }} /></div>
          ) : !isSchool ? (
            <div className="sp-card"><EmptyState icon="🔒" title={t("notAuthorized")} /></div>
          ) : (
            <>
              {/* ══════════ PROFESORES ══════════ */}
              {view === "teachers" && (
                <div className="sp-card">
                  <div className="sp-card-head">
                    <h5 className="sp-card-title">{t("teachers")} <span className="sp-count">{teachers.length}</span></h5>
                    <button type="button" className="sp-btn sp-btn-soft" onClick={() => openImport("teachers")}>
                      <AiOutlineImport /> {t("importTeachers")}
                    </button>
                  </div>
                  <Form onSubmit={createTeacher} className="sp-form">
                    <Row className="g-2">
                      <Col md={4}><Form.Control className="sp-input" placeholder={t("teacherName")} value={tName} onChange={(e) => setTName(e.target.value)} /></Col>
                      <Col md={2} xs={4}><Form.Select className="sp-input" value={tDocType} onChange={(e) => setTDocType(e.target.value)}>{DOC_TYPES.map((d) => <option key={d}>{d}</option>)}</Form.Select></Col>
                      <Col md={6} xs={8}><Form.Control className="sp-input" placeholder={`${t("docNumber")} (${t("optional")})`} value={tDocNumber} onChange={(e) => setTDocNumber(e.target.value.replace(/\D/g, ""))} inputMode="numeric" /></Col>
                      <Col md={5} xs={12}><Form.Control className="sp-input" type="email" placeholder={t("teacherEmail")} value={tEmail} onChange={(e) => setTEmail(e.target.value)} required /></Col>
                      <Col md={4} xs={8}><Form.Control className="sp-input" value={tPass} onChange={(e) => setTPass(e.target.value)} required /></Col>
                      <Col md={3} xs={4}><button type="button" className="sp-btn sp-btn-soft w-100" onClick={() => setTPass(genPassword())}>{t("generate")}</button></Col>
                      <Col xs={12}><button type="submit" className="sp-btn sp-btn-primary w-100" disabled={savingT}>{savingT ? <Spinner animation="border" size="sm" /> : <><AiOutlinePlus /> {t("addTeacher")}</>}</button></Col>
                    </Row>
                  </Form>
                  {teachers.length === 0 ? (
                    <EmptyState icon="👩‍🏫" title={t("noTeachers")} hint={t("teachersEmptyHint")} />
                  ) : (
                    <>
                      <div className="sp-tablewrap">
                        <Table className="sp-table mb-0">
                          <thead><tr>
                            <th style={{ width: 34 }}>#</th><th>{t("teacherName")}</th>
                            <th>{t("teacherEmail")}</th><th className="text-end">{t("actionsCol")}</th>
                          </tr></thead>
                          <tbody>
                            {teachersPage.map((tc, i) => (
                              <tr key={tc.uid}>
                                <td className="sp-idx">{(pT - 1) * PER_PAGE + i + 1}</td>
                                <td>
                                  <div className="sp-person"><Monogram name={tc.displayName || tc.email} />
                                    <div><div className="sp-person-name">{tc.displayName || "—"}</div>
                                      {tc.docNumber ? <div className="sp-sub sp-mono">{tc.docType}-{tc.docNumber}</div> : null}</div>
                                  </div>
                                </td>
                                <td><span className="sp-email" title={tc.email}>{tc.email}</span></td>
                                <td className="text-end text-nowrap">
                                  <button type="button" className="sp-ic" title={t("viewDetails")} onClick={() => setDetailTeacher(tc)}><AiOutlineEye /></button>
                                  <button type="button" className="sp-ic" title={t("edit")} onClick={() => openEditTeacher(tc)}><AiOutlineEdit /></button>
                                  <button type="button" className="sp-ic" title={t("resetPassword")} onClick={() => resetTeacher(tc)}><MdLockReset /></button>
                                  <button type="button" className="sp-ic sp-ic-danger" title={t("delete")} onClick={() => deleteTeacher(tc)}><AiOutlineDelete /></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      <Pager t={t} page={pT} total={teachers.length} perPage={PER_PAGE} onPage={setTPage} />
                    </>
                  )}
                </div>
              )}

              {/* ══════════ GRADOS ══════════ */}
              {view === "grades" && (
                <div className="sp-card">
                  <div className="sp-card-head"><h5 className="sp-card-title">{t("grades")} <span className="sp-count">{grades.length}</span></h5></div>
                  <Form onSubmit={createGrade} className="sp-form">
                    <Row className="g-2">
                      <Col md={9} xs={8}><Form.Control className="sp-input" placeholder={t("gradeName")} value={gName} onChange={(e) => setGName(e.target.value)} required /></Col>
                      <Col md={3} xs={4}><button type="submit" className="sp-btn sp-btn-primary w-100" disabled={savingG}>{savingG ? <Spinner animation="border" size="sm" /> : <><AiOutlinePlus /> {t("addGrade")}</>}</button></Col>
                    </Row>
                  </Form>
                  {grades.length === 0 ? (
                    <EmptyState icon="📚" title={t("noGrades")} hint={t("gradesEmptyHint")} />
                  ) : (
                    <>
                      <div className="sp-list">
                        {gradesPage.map((g) => {
                          const secCount = sections.filter((s) => s.gradeId === g.id).length;
                          const stuCount = students.filter((s) => s.gradeId === g.id).length;
                          return (
                            <div key={g.id} className="sp-row">
                              <div className="sp-person"><span className="sp-grade-ic"><AiOutlineBook /></span>
                                <div><div className="sp-person-name">{g.name}</div>
                                  <div className="sp-sub">{secCount} {t("sections").toLowerCase()} · {stuCount} {t("students").toLowerCase()}</div></div>
                              </div>
                              <div className="text-nowrap">
                                <button type="button" className="sp-ic" title={t("edit")} onClick={() => renameGrade(g)}><AiOutlineEdit /></button>
                                <button type="button" className="sp-ic sp-ic-danger" title={t("delete")} onClick={() => deleteGrade(g)}><AiOutlineDelete /></button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <Pager t={t} page={pG} total={grades.length} perPage={PER_PAGE} onPage={setGPage} />
                    </>
                  )}
                </div>
              )}

              {/* ══════════ SECCIONES ══════════ */}
              {view === "sections" && (
                <div className="sp-card">
                  <div className="sp-card-head"><h5 className="sp-card-title">{t("sections")} <span className="sp-count">{sections.length}</span></h5></div>
                  {grades.length === 0 ? (
                    <EmptyState icon="🧩" title={t("noGradesYet")} hint={t("sectionsNeedGrade")} />
                  ) : (
                    <Form onSubmit={createSection} className="sp-form">
                      <Row className="g-2">
                        <Col md={7} xs={12}><Form.Control className="sp-input" placeholder={t("sectionName")} value={sName} onChange={(e) => setSName(e.target.value)} required /></Col>
                        <Col md={5} xs={12}>
                          <Form.Select className="sp-input" value={sGradeId} onChange={(e) => setSGradeId(e.target.value)} required>
                            <option value="">{t("selectGrade")}</option>
                            {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                          </Form.Select>
                        </Col>
                        {teachers.length > 0 && (
                          <Col xs={12}>
                            <div className="sp-label">{t("assignTeachers")}</div>
                            <div className="sp-picker">
                              {teachers.map((tc) => (
                                <Form.Check key={tc.uid} type="checkbox" id={`t-${tc.uid}`} label={tc.displayName || tc.email} checked={sTeachers.includes(tc.uid)} onChange={() => toggleTeacher(tc.uid)} />
                              ))}
                            </div>
                          </Col>
                        )}
                        <Col xs={12}><button type="submit" className="sp-btn sp-btn-primary w-100" disabled={savingS}>{savingS ? <Spinner animation="border" size="sm" /> : <><AiOutlinePlus /> {t("addSection")}</>}</button></Col>
                      </Row>
                    </Form>
                  )}
                  {sections.length === 0 ? (
                    grades.length > 0 && <EmptyState icon="🧩" title={t("noSections")} hint={t("sectionsEmptyHint")} />
                  ) : (
                    <>
                      <div className="sp-list">
                        {sectionsPage.map((sec) => (
                          <div key={sec.id} className="sp-row">
                            <div className="sp-person"><span className="sp-sec-ic"><AiOutlineTeam /></span>
                              <div>
                                <div className="sp-person-name">{sec.name} {sec.grade ? <Pill tone="grade">{sec.grade}</Pill> : null}</div>
                                <div className="sp-sub">
                                  <b>{sec.studentCount || 0}</b> {t("students").toLowerCase()}
                                  {sec.teacherIds ? <> · {Object.keys(sec.teacherIds).map(teacherName).join(", ")}</> : null}
                                </div>
                              </div>
                            </div>
                            <div className="text-nowrap">
                              <button type="button" className="sp-ic" title={t("viewDetails")} onClick={() => openDetail(sec)}><AiOutlineEye /></button>
                              <button type="button" className="sp-ic" title={t("edit")} onClick={() => openEditSection(sec)}><AiOutlineEdit /></button>
                              <button type="button" className="sp-ic sp-ic-danger" title={t("delete")} onClick={() => deleteSection(sec)}><AiOutlineDelete /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Pager t={t} page={pS} total={sections.length} perPage={PER_PAGE} onPage={setSPage} />
                    </>
                  )}
                </div>
              )}

              {/* ══════════ ALUMNOS ══════════ */}
              {view === "students" && (
                <div className="sp-card">
                  <div className="sp-card-head">
                    <h5 className="sp-card-title">{t("students")} <span className="sp-count">{students.length}</span></h5>
                    <button type="button" className="sp-btn sp-btn-soft" disabled={grades.length === 0} onClick={() => openImport("students")}>
                      <AiOutlineImport /> {t("importStudents")}
                    </button>
                  </div>
                  {grades.length === 0 ? (
                    <EmptyState icon="🎓" title={t("noGradesYet")} hint={t("studentsNeedGrade")} />
                  ) : (
                    <Form onSubmit={createStudent} className="sp-form">
                      <Row className="g-2">
                        <Col md={5} xs={12}><Form.Control className="sp-input" placeholder={t("studentName")} value={uName} onChange={(e) => setUName(e.target.value)} required /></Col>
                        <Col md={2} xs={4}><Form.Select className="sp-input" value={uDocType} onChange={(e) => setUDocType(e.target.value)}>{DOC_TYPES.map((d) => <option key={d}>{d}</option>)}</Form.Select></Col>
                        <Col md={5} xs={8}><Form.Control className="sp-input" placeholder={`${t("docNumber")} (${t("optional")})`} value={uDocNumber} onChange={(e) => setUDocNumber(e.target.value.replace(/\D/g, ""))} inputMode="numeric" /></Col>
                        <Col md={5} xs={12}><Form.Control className="sp-input" type="email" placeholder={t("studentEmail")} value={uEmail} onChange={(e) => setUEmail(e.target.value)} required /></Col>
                        <Col md={4} xs={8}><Form.Control className="sp-input" value={uPass} onChange={(e) => setUPass(e.target.value)} required /></Col>
                        <Col md={3} xs={4}><button type="button" className="sp-btn sp-btn-soft w-100" onClick={() => setUPass(genPassword())}>{t("generate")}</button></Col>
                        <Col md={6} xs={12}>
                          <Form.Select className="sp-input" value={uGradeId} onChange={(e) => { setUGradeId(e.target.value); setUSectionId(""); }} required>
                            <option value="">{t("selectGrade")} *</option>
                            {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                          </Form.Select>
                        </Col>
                        <Col md={6} xs={12}>
                          <Form.Select className="sp-input" value={uSectionId} onChange={(e) => setUSectionId(e.target.value)} disabled={!uGradeId}>
                            <option value="">{t("noSection")}</option>
                            {sectionsForGrade(uGradeId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </Form.Select>
                        </Col>
                        <Col xs={12}><button type="submit" className="sp-btn sp-btn-primary w-100" disabled={savingU}>{savingU ? <Spinner animation="border" size="sm" /> : <><AiOutlinePlus /> {t("addStudent")}</>}</button></Col>
                      </Row>
                    </Form>
                  )}
                  {students.length === 0 ? (
                    grades.length > 0 && <EmptyState icon="🎒" title={t("noStudents")} hint={t("studentsEmptyHint")} />
                  ) : (
                    <>
                      <div className="sp-tablewrap">
                        <Table className="sp-table mb-0">
                          <thead><tr>
                            <th style={{ width: 34 }}>#</th><th>{t("studentName")}</th>
                            <th>{t("grade")}</th><th>{t("section")}</th><th>{t("studentEmail")}</th><th className="text-end">{t("actionsCol")}</th>
                          </tr></thead>
                          <tbody>
                            {studentsPage.map((s, i) => (
                              <tr key={s.uid}>
                                <td className="sp-idx">{(pU - 1) * PER_PAGE + i + 1}</td>
                                <td>
                                  <div className="sp-person"><Monogram name={s.learnerName} />
                                    <div><div className="sp-person-name">{s.learnerName || "—"}</div>
                                      {s.docNumber ? <div className="sp-sub sp-mono">{s.docType}-{s.docNumber}</div> : null}</div>
                                  </div>
                                </td>
                                <td><Pill tone="grade">{s.grade}</Pill></td>
                                <td><Pill tone="section">{s.section}</Pill></td>
                                <td><span className="sp-email" title={s.email}>{s.email}</span></td>
                                <td className="text-end text-nowrap">
                                  <button type="button" className="sp-ic" title={t("actView")} onClick={() => setActStu(s)}><AiOutlineLineChart /></button>
                                  <button type="button" className="sp-ic" title={t("viewDetails")} onClick={() => setDetailStu(s)}><AiOutlineEye /></button>
                                  <button type="button" className="sp-ic" title={t("resetPassword")} onClick={() => resetStudent(s)}><MdLockReset /></button>
                                  <button type="button" className="sp-ic" title={t("edit")} onClick={() => openEditStudent(s)}><AiOutlineEdit /></button>
                                  <button type="button" className="sp-ic sp-ic-danger" title={t("delete")} onClick={() => deleteStudent(s)}><AiOutlineDelete /></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      <Pager t={t} page={pU} total={students.length} perPage={PER_PAGE} onPage={setUPage} />
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </Container>
        <Footer logged={true} />
      </main>

      {/* Modal: detalle del profesor (datos + secciones asignadas) */}
      <Modal show={!!detailTeacher} onHide={() => setDetailTeacher(null)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 d-flex align-items-center gap-2">
            <span className="sp-sec-ic"><AiOutlineUser /></span>{t("teacherDetail")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailTeacher && (() => {
            const tSecs = sections.filter((sec) => sec.teacherIds && sec.teacherIds[detailTeacher.uid]);
            return (
              <>
                <div className="sp-detail-hero">
                  <Monogram name={detailTeacher.displayName || detailTeacher.email} />
                  <div>
                    <div className="sp-detail-name">{detailTeacher.displayName || "—"}</div>
                    {detailTeacher.docNumber ? <div className="sp-sub sp-mono">{detailTeacher.docType}-{detailTeacher.docNumber}</div> : null}
                  </div>
                </div>
                <div className="sp-kv"><span className="sp-kv-k">{t("teacherEmail")}</span><span className="sp-kv-v">{detailTeacher.email}</span></div>
                <div className="sp-detail-head mt-3"><AiOutlineTeam /> {t("sections")} <span className="sp-count">{tSecs.length}</span></div>
                {tSecs.length === 0 ? <div className="sp-hint">{t("noSectionsAssigned")}</div> : tSecs.map((sec) => (
                  <div key={sec.id} className="sp-detail-row">
                    <span className="sp-sec-ic"><AiOutlineTeam /></span>
                    <div>
                      <div className="sp-person-name">{sec.name} {sec.grade ? <Pill tone="grade">{sec.grade}</Pill> : null}</div>
                      <div className="sp-sub"><b>{sec.studentCount || 0}</b> {t("students").toLowerCase()}</div>
                    </div>
                  </div>
                ))}
              </>
            );
          })()}
        </Modal.Body>
      </Modal>

      {/* Modal: editar profesor */}
      <Modal show={!!editTeacher} onHide={() => setEditTeacher(null)} centered>
        <Modal.Header closeButton><Modal.Title className="fs-5">{t("editTeacher")}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form onSubmit={saveEditTeacher}>
            <Row className="g-2">
              <Col xs={12}><Form.Control className="sp-input" placeholder={t("teacherName")} value={etName} onChange={(e) => setEtName(e.target.value)} required /></Col>
              <Col xs={4}><Form.Select className="sp-input" value={etDocType} onChange={(e) => setEtDocType(e.target.value)}>{DOC_TYPES.map((d) => <option key={d}>{d}</option>)}</Form.Select></Col>
              <Col xs={8}><Form.Control className="sp-input" placeholder={`${t("docNumber")} (${t("optional")})`} value={etDocNumber} onChange={(e) => setEtDocNumber(e.target.value.replace(/\D/g, ""))} inputMode="numeric" /></Col>
              <Col xs={12}><div className="sp-kv"><span className="sp-kv-k">{t("teacherEmail")}</span><span className="sp-kv-v">{editTeacher?.email}</span></div></Col>
              <Col xs={12}><button type="submit" className="sp-btn sp-btn-primary w-100" disabled={savingET}>{savingET ? <Spinner animation="border" size="sm" /> : t("save")}</button></Col>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal: detalle del alumno */}
      <Modal show={!!detailStu} onHide={() => setDetailStu(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 d-flex align-items-center gap-2">
            <span className="sp-sec-ic"><AiOutlineIdcard /></span>{t("studentDetail")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailStu && (
            <>
              <div className="sp-detail-hero">
                <Monogram name={detailStu.learnerName} />
                <div>
                  <div className="sp-detail-name">{detailStu.learnerName || "—"}</div>
                  {detailStu.docNumber ? <div className="sp-sub sp-mono">{detailStu.docType}-{detailStu.docNumber}</div> : null}
                </div>
              </div>
              <div className="sp-kv"><span className="sp-kv-k">{t("grade")}</span><span className="sp-kv-v"><Pill tone="grade">{detailStu.grade}</Pill></span></div>
              <div className="sp-kv"><span className="sp-kv-k">{t("section")}</span><span className="sp-kv-v"><Pill tone="section">{detailStu.section}</Pill></span></div>
              <div className="sp-kv"><span className="sp-kv-k">{t("studentEmail")}</span><span className="sp-kv-v">{detailStu.email}</span></div>
              <div className="sp-kv"><span className="sp-kv-k">{t("colLastAccess")}</span><span className="sp-kv-v">{detailStu.lastAccessTs ? moment.unix(detailStu.lastAccessTs).locale("es").fromNow() : t("neverWord")}</span></div>
              <div className="sp-kv"><span className="sp-kv-k">{t("colConnections")}</span><span className="sp-kv-v">{detailStu.connCount || 0}</span></div>
              <div className="sp-appnote">{t("studentLoginNote")}</div>
              <button type="button" className="sp-btn sp-btn-primary w-100 mt-3" onClick={() => { setActStu(detailStu); setDetailStu(null); }}>
                📈 {t("actView")}
              </button>
            </>
          )}
        </Modal.Body>
      </Modal>

      <StudentActivityModal student={actStu} onHide={() => setActStu(null)} />

      {/* Modal: detalle de la sección (profesores + alumnos) */}
      <Modal show={!!detailSec} onHide={() => setDetailSec(null)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 d-flex align-items-center gap-2">
            <span className="sp-sec-ic"><AiOutlineTeam /></span>
            {detailSec?.name} {detailSec?.grade ? <Pill tone="grade">{detailSec.grade}</Pill> : null}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailSec && (() => {
            const secTeachers = teachers.filter((tc) => detailSec.teacherIds && detailSec.teacherIds[tc.uid]);
            const secStudents = students.filter((s) => s.sectionId === detailSec.id);
            const DPER = 6;
            const dp = Math.min(dPage, Math.max(1, Math.ceil(secStudents.length / DPER)));
            const dStudents = secStudents.slice((dp - 1) * DPER, dp * DPER);
            return (
              <>
                <div className="sp-detail-head"><AiOutlineUser /> {t("teachers")} <span className="sp-count">{secTeachers.length}</span></div>
                {secTeachers.length === 0 ? <div className="sp-hint mb-3">{t("noTeachersAssigned")}</div> : (
                  <div className="mb-3">
                    {secTeachers.map((tc) => (
                      <div key={tc.uid} className="sp-detail-row">
                        <Monogram name={tc.displayName || tc.email} />
                        <div><div className="sp-person-name">{tc.displayName || "—"}</div><div className="sp-sub">{tc.email}</div></div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="sp-detail-head"><AiOutlineIdcard /> {t("students")} <span className="sp-count">{secStudents.length}</span></div>
                {secStudents.length === 0 ? <div className="sp-hint">{t("noStudentsInSection")}</div> : (
                  <>
                    {dStudents.map((s) => (
                      <div key={s.uid} className="sp-detail-row">
                        <Monogram name={s.learnerName} />
                        <div>
                          <div className="sp-person-name">{s.learnerName}
                            {s.docNumber ? <span className="sp-sub sp-mono ms-2">{s.docType}-{s.docNumber}</span> : null}</div>
                          <div className="sp-sub">{s.email}</div>
                        </div>
                      </div>
                    ))}
                    <Pager t={t} page={dp} total={secStudents.length} perPage={DPER} onPage={setDPage} />
                  </>
                )}
              </>
            );
          })()}
        </Modal.Body>
      </Modal>

      {/* Modal: editar sección (con enrolamiento de alumnos) */}
      <Modal show={!!editSec} onHide={() => setEditSec(null)} centered>
        <Modal.Header closeButton><Modal.Title className="fs-5">{t("editSection")}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form onSubmit={saveEditSection}>
            <Row className="g-2">
              <Col xs={7}><Form.Control className="sp-input" placeholder={t("sectionName")} value={eName} onChange={(e) => setEName(e.target.value)} required /></Col>
              <Col xs={5}>
                <Form.Select className="sp-input" value={eGradeId} onChange={(e) => { setEGradeId(e.target.value); setEStudents([]); }}>
                  <option value="">{t("grade")}</option>
                  {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </Form.Select>
              </Col>
              {teachers.length > 0 && (
                <Col xs={12}>
                  <div className="sp-label">{t("assignTeachers")}</div>
                  <div className="sp-picker">
                    {teachers.map((tc) => <Form.Check key={tc.uid} type="checkbox" id={`e-${tc.uid}`} label={tc.displayName || tc.email} checked={eTeachers.includes(tc.uid)} onChange={() => toggleETeacher(tc.uid)} />)}
                  </div>
                </Col>
              )}
              <Col xs={12}>
                <div className="sp-label">{t("enrollStudents")}</div>
                {!eGradeId ? (
                  <div className="sp-hint">{t("pickGradeFirst")}</div>
                ) : (() => {
                  const elig = students.filter((s) => s.gradeId === eGradeId);
                  return elig.length === 0
                    ? <div className="sp-hint">{t("noStudentsInGrade")}</div>
                    : <div className="sp-picker">{elig.map((s) => <Form.Check key={s.uid} type="checkbox" id={`es-${s.uid}`} label={s.learnerName} checked={eStudents.includes(s.uid)} onChange={() => toggleEStudent(s.uid)} />)}</div>;
                })()}
              </Col>
              <Col xs={12}><button type="submit" className="sp-btn sp-btn-primary w-100" disabled={savingE}>{savingE ? <Spinner animation="border" size="sm" /> : t("save")}</button></Col>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal: editar alumno */}
      <Modal show={!!editStu} onHide={() => setEditStu(null)} centered>
        <Modal.Header closeButton><Modal.Title className="fs-5">{t("editStudent")}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form onSubmit={saveEditStudent}>
            <Row className="g-2">
              <Col xs={12}><Form.Control className="sp-input" placeholder={t("studentName")} value={euName} onChange={(e) => setEuName(e.target.value)} required /></Col>
              <Col xs={4}><Form.Select className="sp-input" value={euDocType} onChange={(e) => setEuDocType(e.target.value)}>{DOC_TYPES.map((d) => <option key={d}>{d}</option>)}</Form.Select></Col>
              <Col xs={8}><Form.Control className="sp-input" placeholder={`${t("docNumber")} (${t("optional")})`} value={euDocNumber} onChange={(e) => setEuDocNumber(e.target.value.replace(/\D/g, ""))} inputMode="numeric" /></Col>
              <Col xs={6}>
                <div className="sp-label">{t("grade")}</div>
                <Form.Select className="sp-input" value={euGradeId} onChange={(e) => { setEuGradeId(e.target.value); setEuSectionId(""); }} required>
                  <option value="">{t("selectGrade")}</option>
                  {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </Form.Select>
              </Col>
              <Col xs={6}>
                <div className="sp-label">{t("section")}</div>
                <Form.Select className="sp-input" value={euSectionId} onChange={(e) => setEuSectionId(e.target.value)} disabled={!euGradeId}>
                  <option value="">{t("noSection")}</option>
                  {sectionsForGrade(euGradeId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Form.Select>
              </Col>
              <Col xs={12}><button type="submit" className="sp-btn sp-btn-primary w-100" disabled={savingEU}>{savingEU ? <Spinner animation="border" size="sm" /> : t("save")}</button></Col>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal: importar (profesores o alumnos) */}
      <Modal show={showImport} onHide={closeImport} centered size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5"><AiOutlineImport className="me-2" />{importMode === "students" ? t("importStudents") : t("importTeachers")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {importReport ? (
            <div>
              <div className="d-flex gap-2 flex-wrap mb-3">
                <span className="sp-badge sp-badge-ok">{t("importCreated")}: {importReport.summary.created}</span>
                <span className="sp-badge sp-badge-warn">{t("importSkipped")}: {importReport.summary.skipped}</span>
                <span className="sp-badge sp-badge-err">{t("importFailed")}: {importReport.summary.failed}</span>
              </div>
              {importReport.summary.created > 0 && <button type="button" className="sp-btn sp-btn-primary mb-3" onClick={copyCredentials}>{t("copyCredentials")}</button>}
              <div className="sp-tablewrap" style={{ maxHeight: 340 }}>
                <Table size="sm" className="sp-table mb-0">
                  <thead><tr><th>{t("teacherName")}</th><th>{t("adminEmailLabel")}</th><th>{t("adminPasswordLabel")}</th><th></th></tr></thead>
                  <tbody>
                    {importReport.results.map((r, i) => (
                      <tr key={i}>
                        <td className="small">{r.displayName || r.learnerName || "—"}</td>
                        <td className="small">{r.email}</td>
                        <td className="small">{r.status === "created" ? <code>{r.password}</code> : "—"}</td>
                        <td className="small">{r.status === "created" ? <span className="text-success">✓</span> : <span className="text-danger" title={r.error}>✕ {r.error}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          ) : (
            <div>
              {importMode === "students" && (
                <Row className="g-2 mb-3">
                  <Col xs={6}>
                    <div className="sp-label">{t("grade")} *</div>
                    <Form.Select className="sp-input" value={importGradeId} onChange={(e) => { setImportGradeId(e.target.value); setImportSectionId(""); }}>
                      <option value="">{t("selectGrade")}</option>
                      {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </Form.Select>
                  </Col>
                  <Col xs={6}>
                    <div className="sp-label">{t("section")}</div>
                    <Form.Select className="sp-input" value={importSectionId} onChange={(e) => setImportSectionId(e.target.value)} disabled={!importGradeId}>
                      <option value="">{t("noSection")}</option>
                      {sectionsForGrade(importGradeId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </Form.Select>
                  </Col>
                </Row>
              )}
              <p className="small text-muted mb-2">{t("importHelp")}</p>
              <Form.Control as="textarea" rows={6} value={importText} onChange={(e) => setImportText(e.target.value)}
                placeholder={importMode === "students" ? "Ana Pérez, V-30111222, ana@correo.com\nLuis Gómez, V-30111333, luis@correo.com" : "Jesús Villarroel, V-4649154, jvillarroel@correo.com"}
                style={{ fontFamily: "monospace", fontSize: ".85rem" }} />
              <div className="mt-2">
                <Form.Check type="switch" id="imp-auto" label={t("importAutoEmail")} checked={importAutoEmail} onChange={(e) => setImportAutoEmail(e.target.checked)} />
                {importAutoEmail && (
                  <div className="d-flex align-items-center gap-2 mt-2">
                    <span className="small text-muted">@</span>
                    <Form.Control size="sm" value={importDomain} onChange={(e) => setImportDomain(e.target.value.trim())} style={{ maxWidth: 240 }} />
                    <span className="small text-muted">{t("importAutoEmailHint")}</span>
                  </div>
                )}
              </div>
              {importRows.length > 0 && (
                <>
                  <div className="d-flex justify-content-between align-items-center mt-3 mb-1">
                    <span className="small fw-semibold">{t("importPreview")}</span>
                    <span className="small text-muted">{importValidCount}/{importRows.length} {t("importValid")}</span>
                  </div>
                  <div className="sp-tablewrap" style={{ maxHeight: 240 }}>
                    <Table size="sm" className="sp-table mb-0">
                      <thead><tr><th>{t("teacherName")}</th><th>{t("docNumber")}</th><th>{t("adminEmailLabel")}</th><th></th></tr></thead>
                      <tbody>
                        {importRows.map((r, i) => (
                          <tr key={i} className={r.valid ? "" : "sp-rowbad"}>
                            <td className="small">{r.displayName || <span className="text-danger">—</span>}</td>
                            <td className="small">{r.docNumber ? `${r.docType}-${r.docNumber}` : "—"}</td>
                            <td className="small">{r.email || <span className="text-danger">—</span>}</td>
                            <td className="small">{r.valid ? <span className="text-success">✓</span> : <span className="text-danger" title={r.reason}>✕</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {importReport ? (
            <button type="button" className="sp-btn sp-btn-soft" onClick={closeImport}>{t("close")}</button>
          ) : (
            <>
              <button type="button" className="sp-btn sp-btn-soft" onClick={closeImport}>{t("cancel")}</button>
              <button type="button" className="sp-btn sp-btn-primary" disabled={importing || !importReady} onClick={doImport}>
                {importing ? <Spinner animation="border" size="sm" /> : `${importMode === "students" ? t("importStudents") : t("importTeachers")} (${importValidCount})`}
              </button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <style>{`
        :root { --sp-plum:#632d43; --sp-pink:#eb2178; --sp-ink:#241f2b; --sp-muted:#8b8593; --sp-line:#eceaf0; }
        .sp-wrap { max-width: 1160px; }
        .sp-head { display:flex; align-items:center; gap:14px; }
        .sp-head-ic { width:46px; height:46px; border-radius:14px; display:flex; align-items:center; justify-content:center;
          font-size:1.4rem; color:#fff; background:linear-gradient(135deg,var(--sp-plum),var(--sp-pink)); flex-shrink:0; box-shadow:0 8px 18px rgba(99,45,67,.25); }
        .sp-eyebrow { font-size:.72rem; text-transform:uppercase; letter-spacing:1.2px; color:var(--sp-muted); font-weight:700; }
        .sp-title { font-size:1.7rem; font-weight:800; color:var(--sp-ink); margin:0; letter-spacing:-.5px; }
        .sp-desc { color:var(--sp-muted); margin:10px 0 22px; font-size:.95rem; max-width:640px; }
        .sp-card { background:#fff; border:1px solid var(--sp-line); border-radius:20px; padding:26px; box-shadow:0 4px 24px rgba(36,31,43,.05); }
        .sp-card-head { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; margin-bottom:18px; }
        .sp-card-title { display:flex; align-items:center; gap:10px; font-size:1.15rem; font-weight:700; color:var(--sp-ink); margin:0; }
        .sp-count { background:#f4eef1; color:var(--sp-plum); border-radius:999px; padding:2px 12px; font-size:.82rem; font-weight:800; }
        .sp-form { background:#faf8fb; border:1px solid var(--sp-line); border-radius:14px; padding:16px; margin-bottom:22px; }
        .sp-input { border-radius:10px !important; border:1px solid #e2dfe6 !important; padding:10px 13px !important; font-size:.92rem !important; box-shadow:none !important; height:auto !important; }
        .sp-input:focus { border-color:var(--sp-pink) !important; box-shadow:0 0 0 3px rgba(235,33,120,.12) !important; }
        .sp-label { font-size:.75rem; font-weight:700; text-transform:uppercase; letter-spacing:.5px; color:var(--sp-muted); margin:2px 0 6px; }
        .sp-hint { color:var(--sp-muted); font-size:.85rem; padding:8px 2px; }
        .sp-detail-head { display:flex; align-items:center; gap:8px; font-size:.78rem; font-weight:800; text-transform:uppercase; letter-spacing:.6px; color:var(--sp-plum); margin:6px 0 10px; }
        .sp-detail-row { display:flex; align-items:center; gap:11px; padding:8px 4px; border-bottom:1px solid #f4f2f6; }
        .sp-detail-row:last-child { border-bottom:none; }
        .sp-detail-hero { display:flex; align-items:center; gap:13px; padding:4px 0 14px; margin-bottom:12px; border-bottom:1px solid var(--sp-line); }
        .sp-detail-hero .sp-mono-av { width:46px; height:46px; font-size:1rem; }
        .sp-detail-name { font-size:1.15rem; font-weight:800; color:var(--sp-ink); letter-spacing:-.3px; }
        .sp-kv { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:9px 0; border-bottom:1px solid #f4f2f6; }
        .sp-kv:last-of-type { border-bottom:none; }
        .sp-kv-k { font-size:.72rem; font-weight:800; text-transform:uppercase; letter-spacing:.6px; color:var(--sp-muted); }
        .sp-kv-v { font-size:.92rem; color:var(--sp-ink); font-weight:600; text-align:right; word-break:break-word; }
        .sp-appnote { margin-top:14px; background:#eef7f4; border:1px solid #d6ece5; color:#237a66; border-radius:12px; padding:10px 13px; font-size:.83rem; font-weight:600; }
        .sp-btn { display:inline-flex; align-items:center; justify-content:center; gap:7px; height:auto !important; width:auto !important;
          padding:10px 18px; border-radius:11px; font-size:.9rem; font-weight:700; border:1.5px solid transparent; cursor:pointer; transition:all .18s; line-height:1.1; }
        .sp-btn:disabled { opacity:.55; cursor:default; }
        .sp-btn-primary { background:linear-gradient(135deg,var(--sp-plum),var(--sp-pink)); color:#fff; box-shadow:0 6px 16px rgba(235,33,120,.22); }
        .sp-btn-primary:hover:not(:disabled) { filter:brightness(1.05); transform:translateY(-1px); }
        .sp-btn-soft { background:#f4eef1; color:var(--sp-plum); }
        .sp-btn-soft:hover:not(:disabled) { background:#ece3e8; }
        .sp-picker { max-height:180px; overflow:auto; border:1px solid var(--sp-line); border-radius:12px; padding:10px 14px; columns:2; background:#fff; }
        .sp-picker .form-check { break-inside:avoid; margin-bottom:4px; font-size:.9rem; }
        /* Tabla */
        .sp-tablewrap { overflow-x:auto; border-radius:12px; }
        .sp-table { width:100%; min-width:560px; border-collapse:separate; border-spacing:0; margin:0; }
        .sp-table thead th { font-size:.7rem; text-transform:uppercase; letter-spacing:.6px; color:var(--sp-muted); font-weight:800;
          border-bottom:2px solid var(--sp-line); padding:10px 12px; background:transparent; white-space:nowrap; }
        .sp-table tbody td { padding:11px 12px; border-bottom:1px solid #f4f2f6; vertical-align:middle; font-size:.9rem; color:var(--sp-ink); }
        .sp-table tbody tr:hover td { background:#fcfafb; }
        .sp-idx { color:#c3bcc9; font-weight:700; font-size:.82rem; }
        .sp-person { display:flex; align-items:center; gap:11px; }
        .sp-person-name { font-weight:600; color:var(--sp-ink); }
        .sp-sub { color:var(--sp-muted); font-size:.8rem; margin-top:2px; }
        .sp-email { color:var(--sp-muted); font-size:.85rem; display:inline-block; max-width:190px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; vertical-align:middle; }
        .sp-mono { font-family:'Consolas',monospace; font-size:.85rem; color:#6b6572; white-space:nowrap; }
        .sp-mono-av { width:34px; height:34px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center;
          color:#fff; font-weight:800; font-size:.78rem; flex-shrink:0; letter-spacing:.3px; }
        .sp-grade-ic, .sp-sec-ic { width:34px; height:34px; border-radius:10px; display:inline-flex; align-items:center; justify-content:center; font-size:1.05rem; flex-shrink:0; }
        .sp-grade-ic { background:#f0e9f7; color:#7048e8; }
        .sp-sec-ic { background:#e7f6f2; color:#2e7d6b; }
        .sp-pill { display:inline-block; padding:3px 11px; border-radius:999px; font-size:.78rem; font-weight:700; white-space:nowrap; }
        .sp-pill-grade { background:#f0e9f7; color:#6a3ec9; }
        .sp-pill-section { background:#e7f6f2; color:#237a66; }
        .sp-pill-none { background:#f3f2f5; color:#b4aebc; font-weight:600; }
        .sp-ic { width:32px; height:32px; border-radius:9px; border:1px solid var(--sp-line); background:#fff; color:#6b6572;
          display:inline-flex; align-items:center; justify-content:center; cursor:pointer; margin-left:4px; font-size:1rem; transition:all .15s; }
        .sp-ic:hover { background:#f7f5f8; color:var(--sp-plum); border-color:#e2dbe4; }
        .sp-ic-danger:hover { background:#fdecea; color:#d63031; border-color:#f2c4c1; }
        /* Lista (grados/secciones) */
        .sp-list { display:flex; flex-direction:column; }
        .sp-row { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:13px 4px; border-bottom:1px solid #f4f2f6; }
        .sp-row:last-child { border-bottom:none; }
        /* Estado vacío */
        .sp-empty { text-align:center; padding:38px 20px; }
        .sp-empty-ic { font-size:2.4rem; margin-bottom:10px; }
        .sp-empty-t { font-weight:700; color:var(--sp-ink); font-size:1.02rem; }
        .sp-empty-h { color:var(--sp-muted); font-size:.9rem; margin-top:4px; }
        /* Pager */
        .sp-pager { display:flex; align-items:center; justify-content:center; gap:16px; margin-top:18px; }
        .sp-page-btn { width:36px; height:36px; border-radius:10px; border:1.5px solid var(--sp-line); background:#fff; font-size:1.25rem; color:var(--sp-plum); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; }
        .sp-page-btn:disabled { opacity:.35; cursor:default; }
        .sp-page-info { font-size:.85rem; color:var(--sp-muted); font-weight:700; }
        /* Badges reporte import */
        .sp-badge { display:inline-block; padding:5px 13px; border-radius:999px; font-size:.8rem; font-weight:700; }
        .sp-badge-ok { background:#e7f7ee; color:#1b7a43; } .sp-badge-warn { background:#fff6e5; color:#a86a00; } .sp-badge-err { background:#fdecea; color:#c0392b; }
        .sp-rowbad td { background:#fff8f0 !important; }
        @media (max-width:576px){ .sp-picker { columns:1; } .sp-card { padding:18px; } .sp-title { font-size:1.4rem; } }
      `}</style>
    </div>
  );
};

export default SchoolPanel;
